import {
  Box,
  Button,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Typography,
  OutlinedInput,
  Checkbox,
  ListItemText,
} from "@mui/material";
import type { SxProps, Theme } from "@mui/material/styles";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs, { Dayjs } from "dayjs";

export interface ClientReportFiltersProps {
  startDate: string | null;
  endDate: string | null;
  selectedYear: string;
  selectedMonths: number[];
  hasSubmitted: boolean;
  onStartDateChange: (value: string | null) => void;
  onEndDateChange: (value: string | null) => void;
  onYearChange: (value: string) => void;
  onMonthsChange: (value: number[]) => void;
  onSubmit: () => void;
  onClearFilters: () => void;
  /** Merged onto the root filter container (e.g. flex, margin). */
  sx?: SxProps<Theme>;
}

const MONTHS = [
  { value: 1, label: "January" },
  { value: 2, label: "February" },
  { value: 3, label: "March" },
  { value: 4, label: "April" },
  { value: 5, label: "May" },
  { value: 6, label: "June" },
  { value: 7, label: "July" },
  { value: 8, label: "August" },
  { value: 9, label: "September" },
  { value: 10, label: "October" },
  { value: 11, label: "November" },
  { value: 12, label: "December" },
];

const ClientReportFilters = ({
  startDate,
  endDate,
  selectedYear,
  selectedMonths,
  hasSubmitted,
  onStartDateChange,
  onEndDateChange,
  onYearChange,
  onMonthsChange,
  onSubmit,
  onClearFilters,
  sx: sxProp,
}: ClientReportFiltersProps) => {
  const currentYear = new Date().getFullYear();
  const years = Array.from({ length: currentYear - 2023 }, (_, i) => 2024 + i);

  const startDateDayjs = startDate ? dayjs(startDate) : null;
  const endDateDayjs = endDate ? dayjs(endDate) : null;

  const handleStartDateChange = (date: Dayjs | null) => {
    const value = date ? date.format("YYYY-MM-DD") : null;
    onStartDateChange(value);
    if (value) {
      onYearChange("");
      onMonthsChange([]);
    }
  };
  const handleEndDateChange = (date: Dayjs | null) => {
    const value = date ? date.format("YYYY-MM-DD") : null;
    onEndDateChange(value);
    if (value) {
      onYearChange("");
      onMonthsChange([]);
    }
  };

  return (
    <Box
      sx={[
        {
          display: "flex",
          flexDirection: "column",
          gap: 2,
          mb: 3,
          padding: "16px",
          backgroundColor: "#F8F8F8",
          borderRadius: "8px",
          border: "1px solid #E0E0E0",
        },
        ...(sxProp ? (Array.isArray(sxProp) ? sxProp : [sxProp]) : []),
      ]}
    >
      <Typography variant="subtitle2" sx={{ fontWeight: 600, color: "text.secondary" }}>
        Filter by created date
      </Typography>
      <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap", alignItems: "flex-start" }}>
        {/* Custom date range */}
        <Box sx={{ display: "flex", gap: 1, alignItems: "center", flexWrap: "wrap" }}>
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <DatePicker
              label="Start date"
              value={startDateDayjs}
              onChange={handleStartDateChange}
              maxDate={endDateDayjs || undefined}
              format="DD/MM/YYYY"
              slotProps={{ textField: { size: "small", sx: { minWidth: 160, backgroundColor: "#fff" } } }}
            />
            <DatePicker
              label="End date (inclusive)"
              value={endDateDayjs}
              onChange={handleEndDateChange}
              minDate={startDateDayjs || undefined}
              format="DD/MM/YYYY"
              slotProps={{ textField: { size: "small", sx: { minWidth: 160, backgroundColor: "#fff" } } }}
            />
          </LocalizationProvider>
        </Box>

        <FormControl sx={{ minWidth: 120 }} size="small">
          <InputLabel shrink>Year</InputLabel>
          <Select
            value={selectedYear}
            onChange={(e) => onYearChange(e.target.value)}
            label="Year"
            displayEmpty
            renderValue={(v) => (v === "" ? "All" : String(v))}
            sx={{ backgroundColor: "#fff", minWidth: 120 }}
          >
            <MenuItem value="">All</MenuItem>
            {years.map((y) => (
              <MenuItem key={y} value={String(y)}>{y}</MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControl sx={{ minWidth: 220 }} size="small">
          <InputLabel shrink>Month(s)</InputLabel>
          <Select
            multiple
            value={selectedMonths}
            onChange={(e) => {
              const v = e.target.value;
              onMonthsChange(typeof v === "string" ? [] : v);
            }}
            input={<OutlinedInput label="Month(s)" />}
            renderValue={(selected) =>
              selected.length === 0
                ? "All"
                : selected.length === 1
                ? MONTHS.find((m) => m.value === selected[0])?.label ?? String(selected[0])
                : `${selected.length} months`
            }
            sx={{ backgroundColor: "#fff" }}
          >
            {MONTHS.map((m) => (
              <MenuItem key={m.value} value={m.value}>
                <Checkbox checked={selectedMonths.indexOf(m.value) > -1} size="small" />
                <ListItemText primary={m.label} />
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <Button
          onClick={onSubmit}
          variant="contained"
          sx={{
            backgroundColor: "#756DE9",
            color: "#fff",
            borderRadius: "6px",
            padding: "8px 20px",
            textTransform: "capitalize",
            alignSelf: "flex-end",
            "&:hover": { backgroundColor: "#5a4dbf" },
          }}
        >
          Apply
        </Button>

        {hasSubmitted && (
          <Button
            onClick={onClearFilters}
            variant="outlined"
            sx={{
              borderColor: "#756DE9",
              color: "#756DE9",
              borderRadius: "6px",
              padding: "8px 20px",
              textTransform: "capitalize",
              alignSelf: "flex-end",
              "&:hover": { backgroundColor: "rgba(117, 109, 233, 0.08)", borderColor: "#5a4dbf" },
            }}
          >
            Clear filters
          </Button>
        )}
      </Box>
    </Box>
  );
};

export default ClientReportFilters;
